import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import { fetchStudentCharges } from "@/api/students";
import { createWriteOff } from "@/api/finance";
import { QueryState } from "@/components/common/AsyncState";
import { StatusChip } from "@/components/common/StatusChip";
import { CHARGE_STATE_LABELS } from "@/types/enums";
import { formatDate, formatMoney, periodLabel } from "@/utils/format";
import { extractErrorMessage } from "@/types/common";
import { useAuth } from "@/auth/useAuth";
import type { Charge } from "@/types/finance";

function WriteOffDialog({ charge, onClose }: { charge: Charge | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);
  const left = charge ? Math.max(0, charge.left ?? charge.amount - (charge.paid ?? 0)) : 0;

  const mutation = useMutation({
    mutationFn: () => createWriteOff({ chargeId: charge!.id, amount, reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["student-charges"] });
      void queryClient.invalidateQueries({ queryKey: ["debtors"] });
      setAmount(0);
      setReason("");
      onClose();
    },
    onError: (error) => setServerError(extractErrorMessage(error)),
  });

  return (
    <Dialog open={Boolean(charge)} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Списание долга — {charge ? periodLabel(charge.period) : ""}</DialogTitle>
      <DialogContent>
        {serverError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {serverError}
          </Alert>
        )}
        <Stack spacing={2} mt={1}>
          <Typography variant="body2" color="text.secondary">
            Остаток по начислению: {formatMoney(left)}
          </Typography>
          <TextField label="Сумма списания" type="number" value={amount || ""} onChange={(e) => setAmount(Number(e.target.value))} />
          <TextField label="Причина списания" value={reason} onChange={(e) => setReason(e.target.value)} multiline minRows={2} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Отмена
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={!amount || amount > left || reason.trim().length < 2 || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          Списать
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function StudentBillingTab({ studentId }: { studentId: string }) {
  const { hasPermission } = useAuth();
  const canWriteOff = hasPermission("fin.writeoff");
  const [writeOffTarget, setWriteOffTarget] = useState<Charge | null>(null);
  const { data: charges, isLoading, error } = useQuery({
    queryKey: ["student-charges", studentId],
    queryFn: () => fetchStudentCharges(studentId),
  });

  const totalDebt = (charges ?? []).reduce((sum, c) => sum + Math.max(0, c.left ?? c.amount - (c.paid ?? 0)), 0);

  return (
    <QueryState isLoading={isLoading} error={error}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle2">История начислений и оплат</Typography>
        <Typography variant="subtitle2" color={totalDebt > 0 ? "error.main" : "success.main"}>
          Задолженность: {formatMoney(totalDebt)}
        </Typography>
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Период</TableCell>
            <TableCell>Группа</TableCell>
            <TableCell>Сумма начисления</TableCell>
            <TableCell>Оплачено</TableCell>
            <TableCell>Остаток</TableCell>
            <TableCell>Срок оплаты</TableCell>
            <TableCell>Статус</TableCell>
            {canWriteOff && <TableCell align="right">Действие</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {(charges ?? []).map((charge) => {
            const paid = charge.paid ?? 0;
            const left = charge.left ?? charge.amount - paid;
            const status = charge.status ?? (left <= 0 ? "paid" : paid > 0 ? "partial" : "due");
            return (
              <TableRow key={charge.id}>
                <TableCell>{periodLabel(charge.period)}</TableCell>
                <TableCell>{charge.groupCode ?? "—"}</TableCell>
                <TableCell>{formatMoney(charge.amount)}</TableCell>
                <TableCell>{formatMoney(paid)}</TableCell>
                <TableCell>{formatMoney(Math.max(0, left))}</TableCell>
                <TableCell>{formatDate(charge.dueDate)}</TableCell>
                <TableCell>
                  <StatusChip label={CHARGE_STATE_LABELS[status]} />
                </TableCell>
                {canWriteOff && (
                  <TableCell align="right">
                    {left > 0 && (
                      <Button size="small" color="error" onClick={() => setWriteOffTarget(charge)}>
                        Списать
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          {(charges ?? []).length === 0 && (
            <TableRow>
              <TableCell colSpan={canWriteOff ? 8 : 7} align="center">
                Начислений пока нет.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <WriteOffDialog charge={writeOffTarget} onClose={() => setWriteOffTarget(null)} />
    </QueryState>
  );
}
